import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import { billingKeys } from './hooks-billing.js';
import type {
  BillingRunLines, BillingRunReport, BillingRunRequest, BillingRuns, DunningSettings, MonthlyCharges,
} from '@arcnet/contracts';

export const subscriptionKeys = {
  charges: (tenantId: string) => ['monthly-charges', tenantId] as const,
  runs: () => ['billing-runs'] as const,
  runLines: (id: string) => ['billing-runs', id, 'lines'] as const,
};

/** What an account is billed each month, and the current period so far. */
export const useMonthlyCharges = (tenantId: string | null) =>
  useQuery({
    queryKey: subscriptionKeys.charges(tenantId ?? ''),
    queryFn: () => api.get<MonthlyCharges>(`/v1/tenants/${tenantId}/billing/subscriptions`),
    enabled: !!tenantId,
  });

export const useBillingRuns = () =>
  useQuery({
    queryKey: subscriptionKeys.runs(),
    queryFn: () => api.get<BillingRuns>('/v1/billing/runs'),
  });

/** What a run's period bills, worked out now; asked for only when a run is opened. */
export const useBillingRunLines = (id: string | null) =>
  useQuery({
    queryKey: subscriptionKeys.runLines(id ?? ''),
    queryFn: () => api.get<BillingRunLines>(`/v1/billing/runs/${id}/lines`),
    enabled: !!id,
  });

export function useRunBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingRunRequest) =>
      api.post<BillingRunReport>('/v1/billing/runs/actions/run', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: subscriptionKeys.runs() });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** Whether an invoice 21 days overdue suspends the account. Platform staff only. */
export function useSetAutoSuspend(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (autoSuspend: boolean) =>
      api.put<DunningSettings>(`/v1/billing/accounts/${tenantId}/dunning`, { auto_suspend: autoSuspend }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: billingKeys.billing(tenantId) }); },
  });
}
